import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  ComponentRendering,
  Image,
  ImageField,
  Field,
  SitecoreContext,
  RichText,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import { Button, Grid } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import ReactToPrint from 'react-to-print';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import LoadingSection from '../LoadingSection/LoadingSection';
import nextId from 'react-id-generator';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { APIWebstoreContext } from '../../context/globalContext';
import { IReturnReceiptDetails, IReturnReceiptItems } from './IReturnReceipt';
import { PAYMENT_TYPES, PURCHASE_ORDER } from '../../utils/constants';
import DOMPurify from 'dompurify';

export type ReturnReceiptProps = {
  fields: {
    PrintButtonText: Field<string>;
    DownloadButtonText: Field<string>;
    ContactSupport: Field<string>;
    RefundAdditionalText: Field<string>;
    MailingAddressText: Field<string>;
    WireTransferInstruction: Field<string>;
    BillingAddressText: Field<String>;
    LOBSpecificConfiguration: Array<LobConfigurationProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
};

export type LobConfigurationProps = {
  displayName: string;
  fields: {
    LOBIdentifier: Field<string>;
    Logo: ImageField;
    Stamp: ImageField;
  };
};

const ReturnReceipt = ({ fields, rendering }: ReturnReceiptProps): React.JSX.Element => {
  const printRef = useRef<HTMLDivElement>(null);
  const [returnReceiptDetails, setReturnReceiptDetails] = useState<IReturnReceiptDetails>();
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [downloadWidth, setDownloadWidth] = useState('1em');
  const { state } = useContext(APIWebstoreContext);

  const getLobConfig = (lob: string): LobConfigurationProps | undefined => {
    return fields?.LOBSpecificConfiguration?.find(
      (config) => config.fields?.LOBIdentifier?.value === lob
    );
  };

  function replaceText(): Field<string> {
    let refundAdditionalText = fields?.RefundAdditionalText?.value;
    if (refundAdditionalText !== undefined) {
      refundAdditionalText = refundAdditionalText.replace(
        '##CREDIT_CARD_NUMBER##',
        returnReceiptDetails?.payment?.last4DigitsCCNumber || ''
      );
      refundAdditionalText = refundAdditionalText.replace(
        '##AMOUNT##',
        getCurrencyInUSD(returnReceiptDetails?.totalRefund) || ''
      );
      fields.RefundAdditionalText.value = refundAdditionalText;
    }
    return fields?.RefundAdditionalText;
  }

  function convertDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function getSealSection(paymentType?: string, stampField?: ImageField) {
    const renderStampImage = (stampField: ImageField) => {
      if (stampField?.value) {
        return <Image className="stampImage" field={stampField.value} />;
      }
      return <></>;
    };

    return <div className="paid-info">{stampField && renderStampImage(stampField)}</div>;
  }

  function getReturnReceiptDetails() {
    let orderId;
    let orderReturnId;
    let search = window.location.search;
    let params = new URLSearchParams(search);
    if (params.get('orderid') && params.get('orderReturnId')) {
      orderId = params.get('orderid');
      orderReturnId = params.get('orderReturnId');
    }

    if (
      orderId === undefined ||
      orderId === '' ||
      orderReturnId === undefined ||
      orderReturnId === ''
    ) {
      setIsLoading(false);
      return;
    }

    getAPICall(
      `${process.env.REACT_APP_CART_URL}/cartorder/${orderId}/returninvoice/${orderReturnId}`
    )
      .then(function (response: any) {
        if (response && response.status === 200) {
          setReturnReceiptDetails(response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    getReturnReceiptDetails();
  }, []);

  const handleDownload = () => {
    if (typeof window !== 'undefined') {
      const html2pdf = require('html2pdf.js');
      setIsWorking(true);

      const content = printRef.current;

      if (content) {
        const options = {
          margin: 0,
          html2canvas: {
            useCORS: true,
            scale: 4,
            letterRendering: true,
          },
          jsPDF: {
            unit: 'pt',
            format: [content.offsetWidth / 1.3, content.offsetHeight / 1.25],
            orientation: 'portrait',
          },
        };

        html2pdf()
          .set(options)
          .from(content)
          .save(`Receipt-${returnReceiptDetails?.orderId}.pdf`)
          .then(() => setIsWorking(false));
      }
    }
  };

  const InvoicePrinting = (): JSX.Element => {
    let paymentType = returnReceiptDetails?.payment.paymentType;
    let paymentProcessed = returnReceiptDetails?.payment.paymentProcessed;
    let shipToAddress = returnReceiptDetails?.receiptItems?.[0].shipToAddress;

    function displayReturnedEmailAddresses(emailAddresses: Array<string>) {
      let emailAddressList: Array<string> = [];
      var count: any = {};

      emailAddresses.forEach(function (i) {
        count[i.toLowerCase()] = (count[i.toLowerCase()] || 0) + 1;
      });

      for (let [key, value] of Object.entries(count)) {
        emailAddressList.push(`${key} (${value})`);
      }

      return (
        <div key={nextId()}>
          <span key={nextId()}>{emailAddressList.join(', ')}</span>
        </div>
      );
    }

    return (
      <>
        <div className="invoice-content">
          <div className="header red-content">
            Refund Receipt #{returnReceiptDetails?.orderReturnId}
          </div>
          {returnReceiptDetails && getLobConfig(returnReceiptDetails?.lob)?.fields?.Logo && (
            <Image
              className="api-logo"
              field={getLobConfig(returnReceiptDetails.lob)?.fields?.Logo.value}
            />
          )}
        </div>
        <br />

        <div className="flex-start-justifybetween">
          {shipToAddress && (
            <div>
              <div className="sub-header">{fields.MailingAddressText?.value}</div>
              <div className="address-content">
                <div>
                  {shipToAddress.firstName} {shipToAddress.lastName}
                </div>
                <div>{shipToAddress.street1}</div>
                <div>{shipToAddress.street2}</div>
                <div>
                  {shipToAddress.city}, {shipToAddress.state} {shipToAddress.zip}
                </div>
                <div>{shipToAddress.country}</div>
                <div>{shipToAddress.phone}</div>
              </div>
            </div>
          )}

          {returnReceiptDetails?.billingAddress && (
            <div>
              <div className="sub-header">{fields.BillingAddressText?.value}</div>
              <div className="address-content">
                <div>
                  {returnReceiptDetails?.billingAddress?.firstName}{' '}
                  {returnReceiptDetails?.billingAddress?.lastName}
                </div>
                <div>{returnReceiptDetails?.billingAddress?.street1}</div>
                <div>{returnReceiptDetails?.billingAddress?.street2}</div>
                <div>
                  {returnReceiptDetails?.billingAddress?.city},{' '}
                  {returnReceiptDetails?.billingAddress?.state}{' '}
                  {returnReceiptDetails?.billingAddress?.zip}
                </div>
                <div>{returnReceiptDetails?.billingAddress?.country?.toUpperCase()}</div>
                <div>{returnReceiptDetails?.billingAddress?.phone}</div>
              </div>
            </div>
          )}
        </div>
        <br />
        <div className="border-100">
          <Grid container spacing={3}>
            <Grid item>
              <div className="column-header">Date Ordered</div>
              {returnReceiptDetails && convertDate(returnReceiptDetails.dateOrdered)}
            </Grid>

            <Grid item>
              <div className="column-header">Date Refunded</div>
              {returnReceiptDetails && convertDate(returnReceiptDetails.dateRefunded)}
            </Grid>

            <Grid item>
              <div className="column-header">Invoice Number</div>
              {returnReceiptDetails?.orderId}
            </Grid>
            <Grid item>
              <div className="column-header">Refund Total</div>
              {getCurrencyInUSD(returnReceiptDetails?.totalRefund)}
            </Grid>
            <Grid item>
              <div className="column-header">Refund Status</div>
              {(state?.sitecoreDictionaryData && state?.sitecoreDictionaryData?.Completed) ||
                'Completed'}
            </Grid>
            <Grid item>
              <div className="column-header">Refunded to</div>
              {paymentType === PURCHASE_ORDER
                ? PAYMENT_TYPES[returnReceiptDetails?.payment?.paymentMethod ?? '']
                : `Credit Card: ${returnReceiptDetails?.payment?.cardType} ending ${returnReceiptDetails?.payment?.last4DigitsCCNumber}`}
            </Grid>
          </Grid>
        </div>
        <br />
        {returnReceiptDetails &&
          returnReceiptDetails.refundType === 'Partial' &&
          returnReceiptDetails?.receiptItems.map((item: IReturnReceiptItems, id: number) => {
            return (
              <div key={nextId()}>
                <div className="flex-start-justifybetween title-section">
                  <div key={nextId()}>
                    <div className="sub-header" key={nextId()}>
                      {item?.productTitle}
                      {addEllipsis(item?.productTitle)}
                    </div>
                    <div key={nextId()}>Product Code: {item?.productCode}</div>
                  </div>
                  <div className="sub-header refunded-amt" key={nextId()}>
                    {item?.refundAmount && '(' + getCurrencyInUSD(Number(item?.refundAmount)) + ')'}
                  </div>
                </div>
                <Grid container spacing={3} key={nextId()} className="detail-section">
                  {item?.format && (
                    <Grid item key={nextId()} style={{ minWidth: '25%' }}>
                      <div className="column-header" key={nextId()}>
                        Format
                      </div>
                      {item?.format}
                    </Grid>
                  )}
                  <Grid item key={nextId()} style={{ minWidth: '15%' }}>
                    <div className="column-header" key={nextId()}>
                      Returned Quantity
                    </div>
                    {item?.returnedQuantity}
                  </Grid>
                  <Grid item key={nextId()} style={{ minWidth: '15%' }}>
                    <div className="column-header" key={nextId()}>
                      Language
                    </div>
                    {item?.language}
                  </Grid>
                  {item &&
                    item?.revokedEmailAddresses &&
                    item?.revokedEmailAddresses.length > 0 && (
                      <Grid item key={nextId()}>
                        <div className="column-header" key={nextId()}>
                          Returned Recipient Emails
                        </div>
                        {displayReturnedEmailAddresses(item?.revokedEmailAddresses)}
                      </Grid>
                    )}
                  {item && item.isShipmentItem && (
                    <Grid item key={nextId()}>
                      <div className="column-header" key={nextId()}>
                        Returned Shipping Address
                      </div>
                      {item?.shipToAddress && (
                        <div key={nextId()}>
                          <span key={nextId()}>
                            {item?.shipToAddress.street1 && <>{item?.shipToAddress.street1}, </>}
                            {item?.shipToAddress.street2 && <>{item?.shipToAddress.street2}, </>}
                            {item?.shipToAddress.city && <>{item?.shipToAddress.city}, </>}
                            {item?.shipToAddress.state && <>{item?.shipToAddress.state}, </>}
                            {item?.shipToAddress.country && <>{item?.shipToAddress.country}, </>}
                            {item?.shipToAddress.zip !== null && <>{item?.shipToAddress.zip}</>}
                          </span>
                        </div>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={3} className="detail-section detail-date-section">
                  {item.sessionStartDate && (
                    <Grid item className="session-details">
                      <div className="column-header session-dates ">Starts: </div>
                      {getDateTimePartsFromIso(item?.sessionStartDate)?.date}
                    </Grid>
                  )}
                  {item.sessionEndDate && (
                    <Grid item className="session-details">
                      <div className="column-header session-dates">Ends: </div>
                      {getDateTimePartsFromIso(item?.sessionEndDate)?.date}
                    </Grid>
                  )}
                </Grid>
                {item.sessionStartDate && (
                  <Grid container spacing={3} className="description-section">
                    <Grid item>
                      <div className="column-header">Description: </div>
                      <span
                        className="product-value session-desc"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.sessionStartDate || ''),
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
                <br />

                <div className="flex-start-justifybetween title-section">
                  <div className="sub-header partial-refund-content-section" key={nextId()}>
                    {(state?.sitecoreDictionaryData &&
                      state?.sitecoreDictionaryData?.PartialRefund) ||
                      'Partial Refund'}{' '}
                    Complete
                    <span className="partial-return-text" key={nextId()}>
                      Your return of <span className="text-bold">{item.returnedQuantity}</span> out
                      of <span className="text-bold">{item.quantity}</span> is complete. Your refund
                      has been issued.
                    </span>
                  </div>
                </div>
                <br />
                <hr key={nextId()} />
                <br />
              </div>
            );
          })}

        <div className="flex-column-100">
          {returnReceiptDetails && returnReceiptDetails.refundType === 'Partial' && (
            <>
              <div className="flex-start-justifybetween sub-header">
                <div>Refund Amount</div>
                <div className="refunded-amt">
                  {returnReceiptDetails?.refundAmount &&
                    '(' + getCurrencyInUSD(returnReceiptDetails?.refundAmount) + ')'}
                </div>
              </div>
            </>
          )}
          {returnReceiptDetails &&
            (returnReceiptDetails.refundType === 'Partial' ||
              returnReceiptDetails?.refundType === 'Shipping') && (
              <>
                <div className="flex-start-justifybetween sub-header">
                  <div>
                    {(state?.sitecoreDictionaryData &&
                      state?.sitecoreDictionaryData?.ShippingRefund) ||
                      'Shipping Refund'}
                  </div>
                  <div className="refunded-amt">
                    {'(' + getCurrencyInUSD(returnReceiptDetails?.shippingRefund) + ')'}
                  </div>
                </div>
              </>
            )}
          {returnReceiptDetails &&
            (returnReceiptDetails.refundType === 'Partial' ||
              returnReceiptDetails?.refundType === 'Tax') && (
              <>
                <div className="flex-start-justifybetween sub-header">
                  <div>
                    {(state?.sitecoreDictionaryData && state?.sitecoreDictionaryData?.TaxRefund) ||
                      'Tax Refund'}
                  </div>
                  <div className="refunded-amt">
                    {'(' + getCurrencyInUSD(returnReceiptDetails?.taxRefund) + ')'}
                  </div>
                </div>
              </>
            )}
          {returnReceiptDetails &&
            (returnReceiptDetails.refundType === 'Miscellaneous' ||
              returnReceiptDetails?.refundType === 'MembershipDiscount') && (
              <>
                <div className="flex-start-justifybetween sub-header">
                  <div>
                    {returnReceiptDetails.refundType === 'Miscellaneous'
                      ? (state?.sitecoreDictionaryData &&
                          state?.sitecoreDictionaryData?.MiscellaneousRefund) ||
                        'Miscellaneous Refund'
                      : (state?.sitecoreDictionaryData &&
                          state?.sitecoreDictionaryData?.MembershipDiscountRefund) ||
                        'Promotional Discount Refund'}
                  </div>
                  <div className="refunded-amt">
                    {'(' + getCurrencyInUSD(returnReceiptDetails?.miscellaneousRefund) + ')'}
                  </div>
                </div>
                <div className="flex-start-justifybetween sub-header">
                  <div>
                    <p className="refund-reason">
                      Reason for Refund: <span>{returnReceiptDetails?.reasonForRefund}</span>
                    </p>
                  </div>
                </div>
              </>
            )}
        </div>
        <br />
        <hr />
        <br />
        <div className="flex-start-justifybetween sub-header">
          <div>TOTAL REFUND</div>
          <div className="refunded-amt-total">
            {returnReceiptDetails?.totalRefund &&
              '(' + getCurrencyInUSD(returnReceiptDetails?.totalRefund) + ')'}
          </div>
        </div>

        {returnReceiptDetails &&
          getLobConfig(returnReceiptDetails?.lob)?.fields?.Stamp &&
          getSealSection(paymentType, getLobConfig(returnReceiptDetails?.lob)?.fields?.Stamp)}

        <div className="refundAdditionalText">
          <RichText field={replaceText()} />
        </div>
        <br />
      </>
    );
  };

  return (
    <div>
      {isLoading ? (
        <div className="invoiceLoadingSection">
          <LoadingSection open={isLoading} size="fullSize" message="Please wait..." />
        </div>
      ) : returnReceiptDetails?.orderId === undefined || returnReceiptDetails?.orderId === '' ? (
        <>
          <div className="invoice-container">
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <div className="invoice-content">
                  <div className="header red-content">Order Number Not Found</div>
                  {returnReceiptDetails &&
                    getLobConfig(returnReceiptDetails?.lob)?.fields?.Logo && (
                      <Image
                        className="api-logo"
                        field={getLobConfig(returnReceiptDetails.lob)?.fields?.Logo.value}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="invoice-container" ref={printRef}>
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <InvoicePrinting />
              </div>
            </div>
          </div>
          <div className="printAndDownload">
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" name="print" className="ws-primary-button-no-border">
                  {fields.PrintButtonText.value}
                </Button>
              )}
              content={() => printRef.current}
            />

            <Button
              variant="contained"
              name="download"
              className="ws-white-button"
              {...(!isWorking ? { onClick: handleDownload } : {})}
            >
              {isWorking ? <LoadingIcon /> : fields.DownloadButtonText.value}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ReturnReceipt;
